import type { AuthUser } from "@apple-bee/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { ApiError, api } from "./api";
import { AuthScreen } from "./components/Auth";
import {
  allowedPages,
  AppLayout,
  type PageKey,
} from "./components/Layout";
import { LoadingBlock } from "./components/Feedback";
import { AccountingPage } from "./pages/AccountingPage";
import { AdminPage } from "./pages/AdminPage";
import { AuditPage } from "./pages/AuditPage";
import { InventoryPage } from "./pages/InventoryPage";
import { OverviewPage } from "./pages/OverviewPage";
import { PrototypeDataPage } from "./pages/PrototypeDataPage";
import { ShipmentsPage } from "./pages/ShipmentsPage";

const recoveryStorageKey = "applebee_password_recovery_token";
const pageKeys: PageKey[] = [
  "overview",
  "inventory",
  "shipments",
  "accounting",
  "prototype-data",
  "users",
  "audit",
];

function readRecoveryToken(): string | null {
  const url = new URL(window.location.href);
  const token = url.searchParams.get("recover");
  if (token) {
    window.sessionStorage.setItem(recoveryStorageKey, token);
    url.searchParams.delete("recover");
    window.history.replaceState({}, "", url);
    return token;
  }
  return window.sessionStorage.getItem(recoveryStorageKey);
}

function readInitialPage(): PageKey {
  const requested = new URLSearchParams(window.location.search).get("page");
  return pageKeys.includes(requested as PageKey)
    ? (requested as PageKey)
    : "overview";
}

export function App() {
  const queryClient = useQueryClient();
  const modernWorkspace =
    new URLSearchParams(window.location.search).get("modern") === "1";
  const invitationToken = new URLSearchParams(window.location.search).get("invite");
  const [recoveryToken] = useState(readRecoveryToken);
  const meQuery = useQuery({
    queryKey: ["me"],
    queryFn: () => api<{ user: AuthUser }>("/api/auth/me"),
    retry: false,
    enabled: !recoveryToken,
  });
  const [page, setPage] = useState<PageKey>(readInitialPage);

  const user = meQuery.data?.user ?? null;
  const visiblePages = useMemo(() => (user ? allowedPages(user) : []), [user]);

  useEffect(() => {
    if (user && !visiblePages.some((item) => item.id === page)) {
      setPage(visiblePages[0]?.id ?? "overview");
    }
  }, [page, user, visiblePages]);

  async function refreshAuth() {
    await queryClient.invalidateQueries({ queryKey: ["me"] });
    await meQuery.refetch();
  }

  function completeInvitation() {
    const cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.delete("invite");
    window.history.replaceState({}, "", cleanUrl);
    window.location.reload();
  }

  function completeRecovery() {
    window.sessionStorage.removeItem(recoveryStorageKey);
    window.location.reload();
  }

  async function logout() {
    await api("/api/auth/logout", { method: "POST" });
    queryClient.clear();
    await meQuery.refetch();
  }

  if (recoveryToken) {
    return (
      <AuthScreen
        invitationToken={null}
        recoveryToken={recoveryToken}
        onAuthenticated={refreshAuth}
        onInvitationCompleted={completeInvitation}
        onRecoveryCompleted={completeRecovery}
      />
    );
  }

  if (meQuery.isLoading) {
    return (
      <div className="boot-screen">
        <div className="brand-lockup">
          <span className="brand-mark">AB</span>
          <span>
            <strong>Apple Bee</strong>
            <small>Подготавливаем рабочее пространство</small>
          </span>
        </div>
        <LoadingBlock />
      </div>
    );
  }

  if (
    !user &&
    (meQuery.error instanceof ApiError ? meQuery.error.status === 401 : true)
  ) {
    return (
      <AuthScreen
        invitationToken={invitationToken}
        recoveryToken={null}
        onAuthenticated={refreshAuth}
        onInvitationCompleted={completeInvitation}
        onRecoveryCompleted={completeRecovery}
      />
    );
  }

  if (!user) {
    return (
      <div className="boot-screen">
        <h1>Не удалось связаться с сервером</h1>
        <p>Проверьте, что API и PostgreSQL запущены, затем обновите страницу.</p>
        <button className="button button--primary" type="button" onClick={() => void meQuery.refetch()}>
          Повторить
        </button>
      </div>
    );
  }

  if (!modernWorkspace) {
    window.location.replace("/workspace.html");
    return (
      <div className="boot-screen">
        <div className="brand-lockup">
          <span className="brand-mark">AB</span>
          <span>
            <strong>Apple Bee</strong>
            <small>Открываем полное рабочее пространство</small>
          </span>
        </div>
        <LoadingBlock />
      </div>
    );
  }

  return (
    <AppLayout
      user={user}
      currentPage={page}
      onPageChange={setPage}
      onLogout={logout}
    >
      {import.meta.env.VITE_DEMO_MODE === "true" ? (
        <div className="demo-banner">
          Демо-режим: показаны синтетические данные, действия не сохраняются.
        </div>
      ) : null}
      {page === "overview" ? <OverviewPage user={user} /> : null}
      {page === "inventory" ? <InventoryPage user={user} /> : null}
      {page === "shipments" ? <ShipmentsPage user={user} /> : null}
      {page === "accounting" ? <AccountingPage /> : null}
      {page === "prototype-data" ? <PrototypeDataPage user={user} /> : null}
      {page === "users" ? <AdminPage currentUser={user} /> : null}
      {page === "audit" ? <AuditPage /> : null}
    </AppLayout>
  );
}
